import { Skeleton } from 'antd'
import { ReactComponent as RightArrow } from 'assets/icons/blockyRightArrow16.svg'
import cx from 'classnames'
import Avatar from 'common/Avatar/Avatar'
import { Button } from 'components/Button/Button'
import { useRequestReadAccess } from 'hooks/queries/articles/useRequestReadAccess'
import { observer } from 'mobx-react-lite'
import Styles from '../CardViewComponent.module.scss'
import { CardViewProps, SkeletonCardProps } from '../cardViewTypes'
import { getDetailsFromBackground } from '../utils/getDetailsFromBackground'
import { InteractionButtons } from './components/InteractionButtons'

export const DefaultCardView = observer((props: CardViewProps) => {
  const {
    disabled,
    selected,
    commentSelected,
    tasks,
    comments,
    mentionSubject,
    subtitle,
    url,
    cardStyle,
    articleId,
    loading,
    isError,
  } = props
  const { font, background } = cardStyle
  const details = getDetailsFromBackground(background)
  const [requestReadAccess, requestingAccess] = useRequestReadAccess()
  const backgroundColor = isError ? 'linear-gradient(90deg, #EBF0F7 0%, #DCE4F0 100%)' : details.background

  if (loading) return <DefaultCardSkeleton selected={selected} />

  const rightButton = () => {
    if (isError && articleId)
      return (
        <Button
          className={Styles.requestAccessButtonDark}
          isDisabled={requestingAccess}
          variant="tertiary"
          endIcon={RightArrow}
          onPress={() => requestReadAccess(articleId)}
        >
          Request Access
        </Button>
      )
    if (disabled)
      return (
        <Button isDisabled className={Styles.arrowButton}>
          <RightArrow />
        </Button>
      )
    return (
      <a className={Styles.arrowButton} href={url} target="_blank" rel="noopener noreferrer">
        <RightArrow />
      </a>
    )
  }

  return (
    <div
      data-test-id="cardViewComponent"
      style={{ background: backgroundColor }}
      className={`${cx(Styles.cardView, {
        [Styles.selected]: selected,
        [Styles.commentSelected]: commentSelected,
        [Styles.restricted]: isError,
        [Styles.boxShadow]: details.includeBoxShadow && !isError,
        [Styles.whiteText]: details.darkBackground && !isError,
      })}`}
    >
      <div className={Styles.cardViewRight}>
        <Avatar className={Styles.avatar} size={36} {...mentionSubject.avatar} />
        <div className={Styles.titleContainer}>
          <p className={Styles.title} style={{ fontFamily: font }}>
            {mentionSubject.title}
          </p>
          <p className={Styles.subtitle} style={{ fontFamily: font }}>
            {subtitle}
          </p>
        </div>
      </div>
      <div className={Styles.cardViewLeft}>
        <InteractionButtons disabled={disabled} tasks={tasks} comments={comments} />
        {rightButton()}
      </div>
    </div>
  )
})

const DefaultCardSkeleton = ({ selected }: SkeletonCardProps) => {
  return (
    <div
      data-test-id="cardViewComponent"
      className={`${cx(Styles.cardView, Styles.loading, {
        [Styles.selected]: selected,
      })}`}
    >
      <div className={Styles.cardViewRight}>
        <Avatar className={Styles.avatar} size={36} />
        <div className={Styles.titleContainer}>
          <Skeleton className={Styles.title} paragraph={false} title={false} />
          <Skeleton className={Styles.subtitle} paragraph={false} title={false} />
        </div>
      </div>
      <div className={Styles.cardViewLeft} />
    </div>
  )
}
